import { connect, useDispatch } from 'react-redux';
import { Dispatch, useCallback } from 'react';
import { getIn } from 'final-form';

import { IKnmErknmFormReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormReason';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import {
  getKnmIsEditableInStatusCommentsExist,
  getKnmIsErknmStatusCommentsExist,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmOrdersSelectOptions,
  getKnmWatchOnlyByTestUser,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { IKnmReasonTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonTypeSelectOption';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IDictionaryVersion } from 'APIPackage/DictionariesModule/interfaces/IDictionaryVersion';
import { fetchDictionaryValue } from 'APIPackage/DictionariesModule/services/dictionariesApi';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { CHECKING_IT_ACCREDITATION } from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnmOrderSelectOption } from 'KnmPackage/MainModule/interfaces/IKnmOrderSelectOption';
import { getFullNameGuarantor } from 'KnmPmPackage/PmReasonsModule/common/getFullNameGuarantor';
import { catalogOrdersRelationDictionariesGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { isAssignmentRequiredByType } from 'KnmPackage/MainModule/common/isAssignmentRequiredByType';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { IKnmLink } from 'APIPackage/KnmModule/interfaces/IKnmLink';
import { getIsPlanErknmStatusConsidered } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { getKnmStartDate } from 'KnmErknmPackage/MainModule/selectors/knmErknmSelectors';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import styles from './ErknmReasons.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { ErknmLinks } from '../../ErknmEventSubjectModule/components/Links/ErknmLinks';
import { IKnmErknmFormObject } from '../../MainModule/interfaces/IKnmErknmFormObject';
import { IApproveRequiredSelectOption } from '../../MainModule/interfaces/IApproveRequiredSelectOption';
import {
  approveRequiredSelectOption,
  notApproveRequiredSelectOption,
  notificationApproveRequiredSelectOption,
} from '../../MainModule/constants/approveRequiredSelectOptions';
import { isOneReasonLinkRequired } from '../../../KnmPackage/MainModule/common/isOneReasonLinkRequired';
import { ErknmReasonProgramDocument } from './ErknmReasonProgramDocument';
import { knmGetOrders, knmUpdateEvidencesByDecisionLinks } from '../../../KnmPackage/MainModule/actions/knmActions';
import { KnmReasonRiskIndicators } from '../../../KnmPackage/KnmReasonRiskIndicatorModule/components/KnmReasonRiskIndicators';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';
import { useHasFieldApproveRemark } from '../../MainModule/common/useHasFieldApproveRemark';
import { ErknmApproveRemarkSectionCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { ReactQuillEditorField } from '../../../UIPackage/MainModule/components/ReactQuillEditorField';
import { TEXT_REACT_QUILL_EDITOR_BLOCK_ID } from '../../MainModule/constants/knmErknmFieldsElementsIds';
import { ErknmDecisionLinks } from './ErknmDecisionLinks';
import { ErknmEvidence } from './ErknmEvidence/ErknmEvidence';

interface IErknmReasonItemProps {
  reasonFieldValue: IKnmErknmFormReason | undefined;
  reasonsFieldName: string;
  sIndex: number;
  typesSelectOptions: IKnmReasonTypeSelectOption[] | undefined;
  isNewKnm: boolean;
  formRef?: IKnmFormRef;
  watchOnly: boolean;
  isDisabled?: boolean;
  created: string | null | undefined;
  isCreateModal?: boolean;
}

interface IErknmReasonItemStoreProps {
  isSupervisor: boolean;
  isEditableInStatusCommentsExist: boolean;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  knmOrders: IKnmOrderSelectOption[] | undefined;
  settings: IAdminSetting[] | undefined;
  isWatchOnlyByTestUser: boolean;
  isErknmStatusCommentsExist: boolean;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  startDate: string | undefined;
}

const ErknmReasonItemWithoutStore = ({
  reasonFieldValue,
  reasonsFieldName,
  sIndex,
  typesSelectOptions,
  watchOnly,
  created,
  formRef,
  isEditableInStatusCommentsExist,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isNewKnm,
  isSupervisor,
  knmOrders,
  settings,
  isDisabled = false,
  isWatchOnlyByTestUser,
  isCreateModal,
  isErknmStatusCommentsExist,
  knmPlanStatus,
  startDate,
}: IErknmReasonItemProps & IErknmReasonItemStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const isReasonsRemarksExists = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.REASONS);
  const isKindKnmRemarksExists = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.KIND_KNM);
  const isDatesRemarksExists = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.DATES);
  const isKnmKindRemarkExist = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.KIND_KNM);
  const isKnmPlanStatusConsidered = getIsPlanErknmStatusConsidered(knmPlanStatus);
  const remove = formRef?.current.mutators.remove;
  const order = reasonFieldValue?.order;
  const isActiveOrderHelpText = order?.flagInagency || order?.flagIt || order?.flagMsp || order?.flagSonko;
  const { knoOrganizationErknm, kindControl, kindKnm, ...otherValues } = formRef?.current.getState().values ?? {};

  const passportLinksName = `${reasonsFieldName}.passportLinks`;
  const violationEvidencesName = `${reasonsFieldName}.violationEvidences`;

  const passportLinks: IKnmLink[] | undefined = getIn(otherValues, passportLinksName);

  const isShowViolationEvidence = Array.isArray(passportLinks) && passportLinks?.length > 0;

  const isLaterThanOrderInProd = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATION_4_1_39_STARTS_FROM,
  );
  const isLaterThanReasonFieldsAvailabilitySetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.REASON_FIELDS_AVAILABILITY_4_1_54_STARTS_FROM,
  );
  const isShowCheckingProgram = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_41_STARTS_FROM,
  );

  const ervkUuid = reasonFieldValue?.ervkUuid;

  const isShowKnmLinks =
    ervkUuid || isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_REASON_4_1_54_STARTS_FROM);

  const isShowEvidenceSetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_EVIDENCE_4_1_25_STARTS_FROM,
  );

  const isShowDecisionLinks =
    reasonFieldValue?.type?.name === KnmReasonTypeNameEnum.IMMEDIATE_CONTROL_PURCHASE && isShowEvidenceSetting;

  const isReasonFieldsEnable =
    (isCreateModal ? true : !isWatchOnlyByTestUser) &&
    isSupervisor &&
    !isDisabled &&
    (isNewKnm ||
      isKnmStatusFillInProcess ||
      isKnmStatusReadyForApprove ||
      isEditableInStatusCommentsExist ||
      (isErknmStatusCommentsExist && (isReasonsRemarksExists || isKindKnmRemarksExists || isDatesRemarksExists)));

  /**
   * Обработчик изменения основания проведения
   */
  const onChangeReason = useCallback(
    async (value: IKnmReasonTypeSelectOption) => {
      if (value.ervkUuid && typeof value?.agreement === 'string') {
        let option: IApproveRequiredSelectOption | undefined;

        if (value.agreement === notificationApproveRequiredSelectOption.agreementValue) {
          option = notificationApproveRequiredSelectOption;
        } else if (value.agreement === notApproveRequiredSelectOption.agreementValue) {
          option = notApproveRequiredSelectOption;
        } else if (value.agreement === approveRequiredSelectOption.agreementValue) {
          option = approveRequiredSelectOption;
        }

        formRef?.current.change('approveRequired', option);
      } else {
        formRef?.current.change('approveRequired');
      }
    },
    [formRef],
  );

  const handleReasonTypeChange = (index: number) => async (value: IKnmReasonTypeSelectOption) => {
    formRef?.current.change(`reasonsErknm[${index}].reason` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].ervkUuid` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].title` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].shortTitle` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].text` as keyof IKnmErknmForm);

    if (value) {
      await onChangeReason(value);
    }

    formRef?.current.change(`reasonsErknm[${index}].requirementDetails` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].dateRequirement` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].assignmentNumber` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].assignmentDate` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].requirementDetails` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].riskIndicators` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].order` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].fullName` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].indicatorRiskAttachment` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].indicatorRiskAuto` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].links` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].programAttachment` as keyof IKnmErknmForm);
    formRef?.current.change(`reasonsErknm[${index}].programNumber` as keyof IKnmErknmForm);

    if (value?.ervkUuid) {
      const dictionaryValue: IDictionaryVersion = await fetchDictionaryValue(DictionaryUUIDsEnum.ERVK, value.ervkUuid);

      const reasonRequirementsValue = JSON.parse(dictionaryValue.value);

      formRef?.current.change(`reasonRequirements` as keyof IKnmErknmForm, reasonRequirementsValue);
      formRef?.current.change(`reasonsErknm[${index}].ervkUuid` as keyof IKnmErknmForm, value?.ervkUuid);

      const organizations = formRef?.current.getFieldState('organizations')?.value;

      if (organizations?.length && reasonRequirementsValue?.it !== null) {
        const hasItOrganization = !!organizations?.find(i => i.isPresentInItRegistry);
        const hasNotItOrganization = !!organizations?.find(i => !i.isPresentInItRegistry);

        if (
          (reasonRequirementsValue?.it && hasNotItOrganization) ||
          (!reasonRequirementsValue?.it && hasItOrganization)
        ) {
          dispatch(
            addNoticeAction({
              renderContent: CHECKING_IT_ACCREDITATION,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.CHECKING_IT_ACCREDITATION,
            }),
          );
        }
      }
    }

    // @ts-ignore
    const objects: IKnmErknmFormObject[] | undefined = formRef?.current.getFieldState(
      'objectsErknm' as keyof IKnmErknmForm,
    )?.value;

    if (!isKnmPlanStatusConsidered && !isKnmKindRemarkExist) {
      formRef?.current.change(`organizations` as keyof IKnmErknmForm);

      objects?.forEach((item, i) => {
        if (item.ervkId) {
          !watchOnly && remove && remove('objectsErknm', i);
        } else {
          formRef?.current.change(`objectsErknm[${i}].objectType` as keyof IKnmErknmForm);
          formRef?.current.change(`objectsErknm[${i}].objectKind` as keyof IKnmErknmForm);
          formRef?.current.change(`objectsErknm[${i}].objectSubKind` as keyof IKnmErknmForm);
          formRef?.current.change(`objectsErknm[${i}].riskCategory` as keyof IKnmErknmForm);
          formRef?.current.change(`objectsErknm[${i}].dangerClass` as keyof IKnmErknmForm);
        }
      });
    }

    if (isNewKnm) {
      formRef?.current.change(`kindKnm` as keyof IKnmErknmForm);
    }

    if (!isCreateModal) {
      formRef?.current.change(`startTime` as keyof IKnmErknmForm);
      formRef?.current.change(`stopTime` as keyof IKnmErknmForm);
    }
  };

  const clearReasonCallback = useCallback(
    (fieldName: string) => {
      formRef?.current.change('approveRequired');
      // @ts-ignore
      formRef?.current.change(`${fieldName}`);
      formRef?.current.change(`organizations` as keyof IKnmErknmForm);
    },
    [formRef],
  );

  const handleUpdateDecisionEvidencesAction = useCallback(() => {
    if (passportLinks && passportLinks.length > 0) {
      dispatch(knmUpdateEvidencesByDecisionLinks(passportLinks));
    }
  }, [dispatch, passportLinks]);

  const makeRemoveReasonHandler = (i: number) => () => {
    if (!watchOnly && remove) {
      remove('reasonsErknm', i);
      formRef?.current.change(`organizations` as keyof IKnmErknmForm);
    }
  };

  const isAssignment = (reason: IKnmErknmFormReason) => {
    if (isLaterThanOrderInProd) {
      return !!reason.order;
    }

    return (reason.reason?.assignmentNumber || reason.reason?.assignmentDate) && !reason.order;
  };

  const getOrderOptions = () => () => {
    const { typeErknm } = formRef?.current.getState().values ?? {};

    if (
      knoOrganizationErknm &&
      kindControl &&
      typeErknm &&
      reasonFieldValue &&
      isAssignmentRequiredByType(reasonFieldValue.type)
    ) {
      const currentErvkUuid = reasonFieldValue?.ervkUuid;

      dispatch(
        knmGetOrders(
          String(knoOrganizationErknm?.value.dictVersionId),
          String(kindControl?.value.dictVersionId),
          String(currentErvkUuid),
          false,
          typeErknm.code,
        ),
      );
    }
  };

  const handleOrderChange = (index: number) => (value?: IKnmOrderSelectOption) => {
    const { number, date, guarantors, recordId, recordVersionId } = value || {};

    dispatch(catalogOrdersRelationDictionariesGet(recordId, recordVersionId));

    const objectsErknm = formRef?.current.getFieldState('objectsErknm')?.value;
    const eventsErknm = formRef?.current.getFieldState('eventsErknm')?.value;
    const organizations = formRef?.current.getFieldState('organizations')?.value;
    const newObjectsErknm = objectsErknm
      ?.filter(objectErknm => !objectErknm.ervkId)
      .map(objectErknm => ({
        ...objectErknm,
        objectType: undefined,
        objectKind: undefined,
        objectSubKind: undefined,
        riskCategory: undefined,
        dangerClass: undefined,
      }));
    const newOrganizations = organizations?.map(item => ({
      ...item,
      subjectType: undefined,
      inn: undefined,
      snils: undefined,
      organizationName: undefined,
      ogrn: undefined,
    }));

    const newEventsErknm = eventsErknm?.map(item => ({ ...item, type: undefined }));

    formRef?.current.change('objectsErknm', newObjectsErknm);
    formRef?.current.change('eventsErknm', newEventsErknm);
    formRef?.current.change('organizations', newOrganizations);
    isNewKnm && formRef?.current.change('kindKnm');

    formRef?.current.change(`reasonsErknm[${index}].assignmentNumber` as keyof IKnmErknmForm, number);
    formRef?.current.change(`reasonsErknm[${index}].assignmentDate` as keyof IKnmErknmForm, date);
    formRef?.current.change(
      `reasonsErknm[${index}].fullName` as keyof IKnmErknmForm,
      getFullNameGuarantor(guarantors?.[0]),
    );
  };

  const makeRemoveReasonOrder = (index: number) => () => {
    if (!watchOnly) {
      formRef?.current.change(`reasonsErknm[${index}].order` as keyof IKnmErknmForm);
      handleOrderChange(index)();
    }
  };

  const isReasonFieldsDisabled = isLaterThanReasonFieldsAvailabilitySetting ? !isReasonFieldsEnable : watchOnly;

  const IRAssignmentNumber = reasonFieldValue?.order?.inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ERVK_ORDER_NUMBER,
  );
  const IRAssignmentDate = reasonFieldValue?.order?.inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ERVK_ORDER_DATE,
  );

  const isReasonTypeDisabled =
    !isCreateModal &&
    !(
      isSupervisor &&
      isErknmStatusCommentsExist &&
      (isReasonsRemarksExists || isKindKnmRemarksExists || isDatesRemarksExists)
    );

  const parseStartDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

  return (
    <>
      <div className="shared-deleting-row">
        <SelectInputField
          disabled={isReasonTypeDisabled}
          id={`${reasonsFieldName}.type`}
          isClearable={!isReasonTypeDisabled && !!reasonFieldValue?.type}
          isSearchable
          isShowTooltip={true}
          name={`${reasonsFieldName}.type`}
          onChange={handleReasonTypeChange(sIndex)}
          onClear={() =>
            !isReasonTypeDisabled && clearReasonCallback && clearReasonCallback(`${reasonsFieldName}.type`)
          }
          options={typesSelectOptions}
          placeholder={
            isNewKnm ? (
              'Выберите значение'
            ) : (
              <>
                Выберите основание регистрации КНМ
                <ErknmFieldMark fieldName={`${reasonsFieldName}.type`} />
              </>
            )
          }
          size="medium"
          tooltipWidth={document.getElementById(`${reasonsFieldName}.type`)?.offsetWidth}
          validate={makeRequired('Поле не может быть пустым')}
        />
        {!isNewKnm && (
          <>
            {reasonFieldValue?.type?.hasText && (
              <>
                <FieldBlock
                  label={
                    <>
                      Дополнительно
                      <ErknmFieldMark fieldName={`${reasonsFieldName}.textReactQuillEditor`} />
                    </>
                  }
                  color="black"
                  margin="top"
                >
                  <ReactQuillEditorField
                    className={styles.ReasonTextField}
                    disabled={isReasonFieldsDisabled}
                    id={TEXT_REACT_QUILL_EDITOR_BLOCK_ID}
                    name={`${reasonsFieldName}.textReactQuillEditor`}
                    handleCustomChange={(e: string) => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      formRef?.current?.change(`${reasonsFieldName}.text` as any, e);
                    }}
                  />
                </FieldBlock>
              </>
            )}
            {reasonFieldValue?.type?.indicatorRiskRequired && (
              <KnmReasonRiskIndicators
                isPm={false}
                formRef={formRef}
                isWatchOnly={isReasonFieldsDisabled}
                reasonFieldValue={reasonFieldValue}
                reasonsFieldName={reasonsFieldName}
              />
            )}
            {(reasonFieldValue?.type?.requirementDetailsRequired || reasonFieldValue?.type?.detailsRequirement) &&
              reasonFieldValue?.type?.dateRequirement && (
                <div className={styles.ReasonAssignmentRow}>
                  <Row notWrap>
                    <FieldBlock
                      label={
                        <>
                          Дата требования
                          <ErknmFieldMark fieldName={`${reasonsFieldName}.dateRequirement`} />
                        </>
                      }
                      placementLabel="left"
                      color="black"
                      type="not-space"
                    >
                      <DatePickerField
                        className={styles.ReasonDateField}
                        disabled={isReasonFieldsDisabled}
                        id={`${reasonsFieldName}.dateRequirement`}
                        name={`${reasonsFieldName}.dateRequirement`}
                        placeholder="Выберите дату"
                        validate={makeRequired('Поле не может быть пустым')}
                        maxDate={parseStartDate}
                      />
                    </FieldBlock>
                    <FieldBlock
                      label={
                        <>
                          Реквизиты требования
                          <ErknmFieldMark fieldName={`${reasonsFieldName}.requirementDetails`} />
                        </>
                      }
                      placementLabel="left"
                      color="black"
                      size="half"
                    >
                      <TextareaField
                        className="shared-row-field"
                        disabled={isReasonFieldsDisabled}
                        id={`${reasonsFieldName}.requirementDetails`}
                        name={`${reasonsFieldName}.requirementDetails`}
                      />
                    </FieldBlock>
                  </Row>
                </div>
              )}
            {!reasonFieldValue?.type?.assignmentNumberRequired &&
              !reasonFieldValue?.type?.approveRequired &&
              reasonFieldValue?.type?.dateRequired && (
                <div className={styles.ReasonAssignmentRow}>
                  <FieldBlock
                    size="half"
                    label={
                      <>
                        Дата
                        <ErknmFieldMark fieldName={`${reasonsFieldName}.date`} />
                      </>
                    }
                    placementLabel="left"
                    color="black"
                  >
                    <DatePickerField
                      className="shared-row-field"
                      disabled={watchOnly}
                      id={`${reasonsFieldName}.date`}
                      name={`${reasonsFieldName}.date`}
                    />
                  </FieldBlock>
                </div>
              )}

            {isAssignmentRequiredByType(reasonFieldValue?.type) && (
              <FieldBlock margin="top">
                <div className="shared-field-row">
                  <div className="shared-row-flex">
                    {!order && isLaterThanOrderInProd && (
                      <Row>
                        <FieldBlock
                          label={
                            <>
                              Выберите поручение
                              <ErknmFieldMark fieldName={`${reasonsFieldName}.order`} />
                            </>
                          }
                          placementLabel="left"
                          color="black"
                          labelSize="col-4"
                        >
                          <SelectInputField
                            className="shared-row-field"
                            disabled={isReasonFieldsDisabled}
                            id={`${reasonsFieldName}.order`}
                            isSearchable
                            name={`${reasonsFieldName}.order`}
                            options={knmOrders}
                            onChange={handleOrderChange(sIndex)}
                            onOpen={getOrderOptions()}
                            placeholder="Введите номер, дату или полное наименование поручения"
                            size="medium"
                          />
                        </FieldBlock>
                        {isActiveOrderHelpText && (
                          <Hint text="Статус лица в реестре противоречит требованиям поручения" />
                        )}
                      </Row>
                    )}
                    {reasonFieldValue && isAssignment(reasonFieldValue) && (
                      <>
                        <Row>
                          <FieldBlock
                            size="half"
                            label="Номер поручения"
                            placementLabel="left"
                            color="black"
                            margin="right"
                          >
                            <TextInputField
                              className="shared-row-field"
                              id={`${reasonsFieldName}.assignmentNumber`}
                              name={`${reasonsFieldName}.assignmentNumber`}
                              inspectionResult={IRAssignmentNumber}
                              disabled={true}
                            />
                          </FieldBlock>
                          <FieldBlock size="half" label="Дата поручения" placementLabel="left" color="black">
                            <TextInputField
                              className="shared-row-field"
                              id={`${reasonsFieldName}.assignmentDate`}
                              name={`${reasonsFieldName}.assignmentDate`}
                              inspectionResult={IRAssignmentDate}
                              disabled={true}
                            />
                          </FieldBlock>
                        </Row>
                        <Row>
                          <FieldBlock
                            label="ФИО лица, издавшего поручение"
                            color="black"
                            placementLabel="left"
                            margin="top"
                            labelSize="col-4"
                          >
                            <TextareaField
                              resize="none"
                              className="shared-row-field"
                              name={`${reasonsFieldName}.fullName`}
                              disabled={true}
                            />
                          </FieldBlock>
                        </Row>
                      </>
                    )}
                  </div>
                  {order && (
                    <CloseButton
                      className="shared-deleting-row-btn"
                      disabled={!isReasonFieldsEnable}
                      onClick={makeRemoveReasonOrder(sIndex)}
                    />
                  )}
                </div>
              </FieldBlock>
            )}
            {reasonFieldValue?.type?.lastConductedKnmDateRequired && (
              <div className={styles.ReasonDateFieldWrapper}>
                <Row>
                  <FieldBlock
                    size="half"
                    label={
                      <>
                        Дата последней проверки
                        <ErknmFieldMark fieldName={`${reasonsFieldName}.lastConductedKnmDate`} />
                      </>
                    }
                    placementLabel="left"
                    color="black"
                  >
                    <DatePickerField
                      className="shared-row-field"
                      id={`${reasonsFieldName}.lastConductedKnmDate`}
                      name={`${reasonsFieldName}.lastConductedKnmDate`}
                      placeholder="Выберите дату"
                      disabled={!isReasonFieldsEnable}
                      maxDate={parseStartDate}
                    />
                  </FieldBlock>
                </Row>
              </div>
            )}
            {reasonFieldValue?.type?.linkOther && isShowKnmLinks && (
              <ErknmLinks
                isAddDisabled={isOneReasonLinkRequired(reasonFieldValue.type) && !!reasonFieldValue.links?.length}
                isEnabled={true}
                linksName={`${reasonsFieldName}.links`}
                reasonsFieldName={`${reasonsFieldName}`}
                watchOnly={isReasonFieldsDisabled}
                reason={reasonFieldValue}
                created={created}
                controllingOrganizationIds={knoOrganizationErknm?.idStr?.toString() || ''}
                kindControlCode={kindControl?.code || ''}
                kindKnmCode={kindKnm?.code}
              />
            )}
            {/* TODO: 4.1.25 добавить проверку linkOther */}
            {isShowDecisionLinks && (
              <ErknmDecisionLinks
                isEnabled={true}
                linksName={passportLinksName}
                reasonsFieldName={reasonsFieldName}
                watchOnly={isReasonFieldsDisabled}
                reason={reasonFieldValue}
              />
            )}

            {isShowViolationEvidence && (
              <ErknmEvidence
                id={`reasonsErknm[${reasonFieldValue?.listId}].violationEvidences`}
                name={violationEvidencesName}
                watchOnly={isReasonFieldsDisabled}
                formRef={formRef}
                isDecision={false}
                isKnm={true}
                isPm={false}
                onlyLinkView={true}
                disableOriginPassportLinkEditable={true}
                updateAction={handleUpdateDecisionEvidencesAction}
              />
            )}

            {isShowCheckingProgram && reasonFieldValue?.type?.name === KnmReasonTypeNameEnum.CHECKING_PROGRAM && (
              <FieldBlock margin="top">
                <div className="shared-field-row">
                  <div className="shared-row-flex">
                    {reasonFieldValue?.type?.programNumberRequired && (
                      <Row>
                        <FieldBlock
                          label={
                            <>
                              Номер программы проверок
                              <ErknmFieldMark fieldName={`${reasonsFieldName}.programNumber`} />
                            </>
                          }
                          placementLabel="left"
                          color="black"
                        >
                          <TextInputField
                            className="shared-row-field"
                            id={`${reasonsFieldName}.programNumber`}
                            name={`${reasonsFieldName}.programNumber`}
                            disabled={isReasonFieldsDisabled}
                          />
                        </FieldBlock>
                      </Row>
                    )}
                    {reasonFieldValue?.type?.programFileRequired && (
                      <ErknmReasonProgramDocument
                        watchOnly={isReasonFieldsDisabled}
                        name={`${reasonsFieldName}.programAttachment`}
                        programAttachment={reasonFieldValue?.programAttachment}
                        formRef={formRef}
                      />
                    )}
                  </div>
                </div>
              </FieldBlock>
            )}
          </>
        )}
      </div>
      {(isLaterThanReasonFieldsAvailabilitySetting ? isReasonFieldsEnable && !isNewKnm : !isNewKnm) &&
        !isReasonTypeDisabled && (
          <CloseButton
            className="shared-deleting-row-btn"
            disabled={!isCreateModal || watchOnly}
            onClick={makeRemoveReasonHandler(sIndex)}
          />
        )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmReasonItemStoreProps => ({
  isEditableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  knmOrders: getKnmOrdersSelectOptions(state),
  settings: getAdminFetchSettings(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
  isErknmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  startDate: getKnmStartDate(state),
});

export const ErknmReasonItem = connect(mapStateToProps)(ErknmReasonItemWithoutStore);
